import axiosInstance from '../utils/axiosConfig';
import keepServerAnswer from '../utils/keepServerAnswer';

/**
 * Service for handling CRM-related API calls
 */

// Module-level cache to prevent duplicate API calls (especially in React StrictMode)
let partnerDataCache = null;
let partnerDataFetchPromise = null;

export const crmService = {
    /**
     * Fetch all CRM profiles with optional filtering and pagination
     * @param {Object} options - Query options
     * @param {string} options.search - Search query
     * @param {string} options.start_date - Start date for filtering
     * @param {string} options.end_date - End date for filtering
     * @param {number} options.limit - Number of results per page
     * @param {number} options.offset - Offset for pagination
     * @returns {Promise<Object>} - CRM profiles data
     */
    getProfiles: async (options = {}) => {
        try {
            const params = new URLSearchParams();

            // Pass every option through verbatim. Previous version hardcoded
            // ``show_archived=true`` (so we couldn't pass ``all``), dropped
            // ``offset=0`` (because 0 is falsy), and ignored options the
            // dashboard now uses (e.g. ``is_hms_entity``). Iterating keeps
            // this helper future-proof for any new query param the backend
            // accepts without further plumbing.
            Object.entries(options).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                params.append(key, value);
            });

            const url = `/api/crm/profiles/${params.toString() ? '?' + params.toString() : ''}`;
            const response = await axiosInstance.get(url);

            console.log('Successfully fetched CRM profiles:', response.data.length || response.data.count || 'unknown count');
            return response.data;
        } catch (error) {
            console.error('Error fetching CRM profiles:', error);
            throw error;
        }
    },

    /**
     * Fetch the lightweight geocoded-partner projection for the CRM dashboard
     * map tab. Hits ``GET /api/crm/profiles/map-pins/`` which returns only the
     * fields needed to render clustered map markers (no pagination — the
     * payload only covers profiles that actually have coordinates).
     *
     * @param {Object} [options]
     * @param {boolean} [options.showArchived=false] - Include archived partners.
     * @returns {Promise<Array<{id, legal_entity_name, trading_short_name,
     *   lat, lon, approval_status, partner_type_selections}>>}
     */
    getMapPins: async (options = {}) => {
        try {
            const params = {};
            if (options.showArchived) params.show_archived = 'true';
            const response = await axiosInstance.get('/api/crm/profiles/map-pins/', { params });
            const data = Array.isArray(response.data) ? response.data : [];
            console.log(`Successfully fetched ${data.length} CRM map pins`);
            return data;
        } catch (error) {
            console.error('Error fetching CRM map pins:', error);
            throw error;
        }
    },

    /**
     * Fetch partner names for dependencies dropdown
     * @returns {Promise<Array>} - Array of partner names formatted for dropdown
     */
    getPartnerNames: async () => {
        try {
            console.log('Fetching CRM partner names for dependencies dropdown');
            
            // Fetch all profiles with minimal data for performance
            const response = await axiosInstance.get('/api/crm/profiles/');
            
            // Extract and format partner names
            const partnerNames = [];
            
            if (response.data && response.data.results) {
                // Paginated response
                response.data.results.forEach(profile => {
                    const name = profile.trading_short_name 
                        ? `${profile.legal_entity_name} (${profile.trading_short_name})`
                        : profile.legal_entity_name;
                    partnerNames.push(name);
                });
            } else if (Array.isArray(response.data)) {
                // Non-paginated response
                response.data.forEach(profile => {
                    const name = profile.trading_short_name 
                        ? `${profile.legal_entity_name} (${profile.trading_short_name})`
                        : profile.legal_entity_name;
                    partnerNames.push(name);
                });
            }
            
            // Sort alphabetically
            partnerNames.sort();

            console.log(`Successfully fetched ${partnerNames.length} partner names for dependencies dropdown`);
            return partnerNames;
        } catch (error) {
            console.error('Error fetching partner names:', error);
            throw error;
        }
    },

    /**
     * Fetch partner options (id + display name) for dependency pickers that
     * need to persist the linked partner's CRM profile id alongside the label.
     *
     * Uses the same "Legal (Short)" label as getPartnerNames, but returns
     * objects so the caller can store partner_id on each linked entry.
     * getPartnerNames is kept intact for callers that only need the string
     * array (e.g. CRMCommodityTradingTab).
     * @returns {Promise<Array<{id, name}>>} - Array of {id, name} objects
     */
    getPartnerOptions: async () => {
        try {
            console.log('Fetching CRM partner options (id + name) for dependencies dropdown');

            const response = await axiosInstance.get('/api/crm/profiles/');

            // Handle both paginated ({results}) and non-paginated (array) shapes.
            const rows = response.data && response.data.results
                ? response.data.results
                : Array.isArray(response.data)
                ? response.data
                : [];

            const options = rows.map(profile => ({
                id: profile.id,
                name: profile.trading_short_name
                    ? `${profile.legal_entity_name} (${profile.trading_short_name})`
                    : profile.legal_entity_name,
            }));

            // Sort alphabetically by display name
            options.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

            console.log(`Successfully fetched ${options.length} partner options for dependencies dropdown`);
            return options;
        } catch (error) {
            console.error('Error fetching partner options:', error);
            throw error;
        }
    },

    /**
     * Fetch CRM partner data with ID and name for autocomplete dropdowns
     * Uses module-level caching to prevent duplicate requests
     * @returns {Promise<Array>} - Array of {id, name} objects
     */
    getPartnerData: async () => {
        // Return cached data if available
        if (partnerDataCache !== null) {
            console.log('Returning cached CRM partner data');
            return partnerDataCache;
        }
        
        // If a fetch is already in progress, return that promise
        if (partnerDataFetchPromise !== null) {
            console.log('CRM partner data fetch already in progress, awaiting existing request');
            return partnerDataFetchPromise;
        }
        
        // Start new fetch and cache the promise
        partnerDataFetchPromise = (async () => {
            try {
                const response = await axiosInstance.get('/api/crm/profiles/');
                
                // Transform response to include only id and name
                const partnerData = response.data.map(profile => ({
                    id: profile.id,
                    name: profile.legal_entity_name || `Profile ${profile.id}`
                }));
                
                console.log(`Successfully fetched ${partnerData.length} CRM partner data entries`);
                
                // Cache the result
                partnerDataCache = partnerData;
                partnerDataFetchPromise = null;
                
                return partnerData;
            } catch (error) {
                console.error('Error fetching CRM partner data:', error);
                partnerDataFetchPromise = null;
                throw error;
            }
        })();
        
        return partnerDataFetchPromise;
    },
    
    /**
     * Clear the CRM partner data cache (useful for force refresh)
     */
    clearPartnerDataCache: () => {
        partnerDataCache = null;
        partnerDataFetchPromise = null;
        console.log('CRM partner data cache cleared');
    },

    /**
     * Fetch a single CRM profile by ID
     * @param {string|number} profileId - The CRM profile ID
     * @returns {Promise<Object>} - CRM profile data
     */
    getProfile: async (profileId) => {
        try {
            if (!profileId) {
                throw new Error('Profile ID is required');
            }
            
            const response = await axiosInstance.get(`/api/crm/profiles/${profileId}/`);
            
            console.log(`Successfully fetched CRM profile ${profileId}:`, response.data.legal_entity_name);
            return response.data;
        } catch (error) {
            console.error(`Error fetching CRM profile ${profileId}:`, error);
            if (error.response?.status === 404) {
                throw keepServerAnswer(error, 'CRM profile not found');
            }
            throw error;
        }
    },

    /**
     * Create a new CRM profile
     * @param {Object} profileData - The CRM profile data to create
     * @returns {Promise<Object>} - Created CRM profile data
     */
    createProfile: async (profileData) => {
        try {
            if (!profileData.legal_entity_name) {
                throw new Error('Legal entity name is required');
            }
            
            console.log('Creating CRM profile with data:', profileData);
            
            const response = await axiosInstance.post('/api/crm/profiles/', profileData);
            
            console.log(`Successfully created CRM profile: ${response.data.legal_entity_name} (ID: ${response.data.id})`);
            return response.data;
        } catch (error) {
            console.error('Error creating CRM profile:', error);
            
            if (error.response?.data?.errors) {
                // Handle validation errors
                const errorMessages = Object.entries(error.response.data.errors)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                    .join('; ');
                throw keepServerAnswer(error, `Validation failed: ${errorMessages}`);
            }
            
            throw error;
        }
    },

    /**
     * Quick-create a minimal CRM profile from a deal flow (Trade Recap,
     * Delivery, Offer, Fuel Enquiry, etc.).
     *
     * Hits ``POST /api/crm/quick-create-profile/`` which only requires
     * ``IsAuthenticated`` (no ``HasCRMAccess``), so trading users without
     * CRM module access can unblock themselves mid-draft. The returned
     * profile lands in ``approval_status='pending'`` and is tagged with
     * ``created_via='deal_flow'`` for audit. The picker that triggered
     * this call should surface the new profile locally with a "Pending
     * Approval" badge.
     *
     * @param {Object} data
     * @param {string} data.legal_entity_name - Required.
     * @param {string} [data.trading_short_name]
     * @param {string} [data.partner_type] - Single partner type label.
     * @param {string} [data.country] - Free-form country label.
     * @param {string} [data.address] - Invoice capture: the address as printed.
     * @param {string} [data.vat_number] - Invoice capture: the VAT number as printed.
     * @param {string} [data.source] - 'invoice_capture' from Capture Invoice; omit for deal flows.
     * @returns {Promise<Object>} New profile {id, legal_entity_name,
     *   trading_short_name, partner_type_selections, approval_status,
     *   created_via}
     */
    quickCreateProfile: async (data) => {
        const legalEntityName = (data?.legal_entity_name || '').trim();
        if (!legalEntityName) {
            throw new Error('Legal entity name is required');
        }
        try {
            const payload = {
                legal_entity_name: legalEntityName,
                trading_short_name: (data.trading_short_name || '').trim() || undefined,
                partner_type: (data.partner_type || '').trim() || undefined,
                country: (data.country || '').trim() || undefined,
                // Invoice capture only: what the supplier's PDF printed.
                address: (data.address || '').trim() || undefined,
                vat_number: (data.vat_number || '').trim() || undefined,
                source: data.source || undefined,
            };
            const response = await axiosInstance.post('/api/crm/quick-create-profile/', payload);
            console.log(
                `Quick-created CRM profile: ${response.data.legal_entity_name} `
                + `(ID: ${response.data.id}, status: ${response.data.approval_status})`
            );
            return response.data;
        } catch (error) {
            console.error('Error quick-creating CRM profile:', error);
            // Surface the existing profile when the backend reports a duplicate,
            // so the caller can preselect it instead of erroring.
            if (error?.response?.status === 409 && error.response.data?.existing_profile) {
                const existing = error.response.data.existing_profile;
                const conflictError = keepServerAnswer(
                    error,
                    error.response.data.detail || 'CRM profile already exists.'
                );
                conflictError.existingProfile = existing;
                conflictError.isDuplicate = true;
                throw conflictError;
            }
            throw error;
        }
    },

    /**
     * Lightweight CRM profile search for inline partner pickers (recaps,
     * deliveries, offers, fuel enquiries). Hits
     * ``GET /api/crm/quick-search-profiles/`` which only requires
     * ``IsAuthenticated`` (no ``HasCRMAccess``), so trading users without
     * CRM module access can still see existing partners instead of being
     * told "No APPROVED partners found" for every search and creating
     * duplicate stubs.
     *
     * Returns a flat array (no pagination wrapper) projected to the
     * minimum fields the partner-search dropdowns consume.
     *
     * @param {string} searchTerm - Substring to match. Returns [] for
     *   empty / sub-2-char terms (matches old searchProfilesByName).
     * @param {Object} [filters] - Optional extra query params, e.g.
     *   ``{ fuels_debtors_only: 'true' }`` for the bunker customer picker
     *   or ``{ fuels_suppliers_only: 'true' }`` for the supplier picker.
     * @param {number} [pageSize=20] - Max rows to return (cap 100).
     * @returns {Promise<Array<{id, legal_entity_name, trading_short_name,
     *   base_currency, approval_status, monday_account_number,
     *   creditor_debtor_selections, business_categories,
     *   partner_type_selections}>>} Rejects with the server's error when the
     *   search fails -- a failed search is not "no partners" (row 127).
     */
    quickSearchProfiles: async (searchTerm, filters = {}, pageSize = 20) => {
        try {
            const term = (searchTerm || '').trim();
            // Match the old searchProfilesByName behaviour: don't hit the
            // server until the user has typed at least 2 chars. The new
            // endpoint also accepts empty searches (it just lists by
            // legal_entity_name) but the UI was tuned around the 2-char
            // threshold so we preserve it here for parity.
            if (term.length < 2) {
                return [];
            }
            const params = {
                search: term,
                page_size: pageSize,
                ...(filters || {}),
            };
            const response = await axiosInstance.get(
                '/api/crm/quick-search-profiles/',
                { params }
            );
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            console.error('Error in quickSearchProfiles:', error);
            // Row 127: an empty list here read as "no partners found" and
            // offered to create one -- how duplicate partners get made.
            throw error;
        }
    },

    /**
     * Update an existing CRM profile
     * @param {string|number} profileId - The CRM profile ID
     * @param {Object} profileData - The CRM profile data to update
     * @returns {Promise<Object>} - Updated CRM profile data
     */
    updateProfile: async (profileId, profileData) => {
        try {
            if (!profileId) {
                throw new Error('Profile ID is required');
            }
            
            console.log(`Updating CRM profile ${profileId} with data:`, profileData);
            
            // DEBUG: Detailed logging of banking details being sent
            console.log('🏦 crmService.updateProfile: Banking details in request:', {
                banking_details_profiles: profileData.banking_details_profiles,
                banking_details_count: profileData.banking_details_profiles?.length || 0,
                full_banking_data: JSON.stringify(profileData.banking_details_profiles, null, 2)
            });
            
            const response = await axiosInstance.put(`/api/crm/profiles/${profileId}/`, profileData);
            
            console.log(`Successfully updated CRM profile: ${response.data.legal_entity_name} (ID: ${response.data.id})`);
            
            // DEBUG: Log what banking details were returned from backend
            console.log('🏦 crmService.updateProfile: Banking details returned from backend:', {
                returned_banking_details: response.data.banking_details_profiles,
                returned_count: response.data.banking_details_profiles?.length || 0
            });
            
            return response.data;
        } catch (error) {
            console.error(`Error updating CRM profile ${profileId}:`, error);
            
            if (error.response?.data?.errors) {
                // Handle validation errors
                const errorMessages = Object.entries(error.response.data.errors)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                    .join('; ');
                throw keepServerAnswer(error, `Validation failed: ${errorMessages}`);
            }
            
            throw error;
        }
    },

    /**
     * Partially update a CRM profile
     * @param {string|number} profileId - The CRM profile ID
     * @param {Object} profileData - The partial CRM profile data to update
     * @returns {Promise<Object>} - Updated CRM profile data
     */
    patchProfile: async (profileId, profileData) => {
        try {
            if (!profileId) {
                throw new Error('Profile ID is required');
            }
            
            console.log(`Partially updating CRM profile ${profileId} with data:`, profileData);
            
            const response = await axiosInstance.patch(`/api/crm/profiles/${profileId}/`, profileData);
            
            console.log(`Successfully patched CRM profile: ${response.data.legal_entity_name} (ID: ${response.data.id})`);
            return response.data;
        } catch (error) {
            console.error(`Error patching CRM profile ${profileId}:`, error);
            
            if (error.response?.data?.errors) {
                // Handle validation errors
                const errorMessages = Object.entries(error.response.data.errors)
                    .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                    .join('; ');
                throw keepServerAnswer(error, `Validation failed: ${errorMessages}`);
            }
            
            throw error;
        }
    },

    /**
     * Delete a CRM profile
     * @param {string|number} profileId - The CRM profile ID
     * @returns {Promise<Object>} - Delete response with details
     */
    deleteProfile: async (profileId) => {
        try {
            if (!profileId) {
                throw new Error('Profile ID is required');
            }
            
            const response = await axiosInstance.delete(`/api/crm/profiles/${profileId}/`);
            
            console.log(`Successfully deleted CRM profile ${profileId}`);
            console.log('Delete response:', response.data);
            
            // Return the full response to include deletion details
            return response;
        } catch (error) {
            console.error(`Error deleting CRM profile ${profileId}:`, error);
            if (error.response?.status === 404) {
                throw keepServerAnswer(error, 'CRM profile not found');
            }
            throw error;
        }
    },

    /**
     * Archive a CRM profile
     * @param {string|number} profileId - The CRM profile ID
     * @returns {Promise<Object>} - Archive response with details
     */
    archiveProfile: async (profileId) => {
        try {
            if (!profileId) {
                throw new Error('Profile ID is required');
            }
            
            const response = await axiosInstance.post(`/api/crm/profiles/${profileId}/archive/`);
            
            console.log(`Successfully archived CRM profile ${profileId}`);
            console.log('Archive response:', response.data);
            
            return response;
        } catch (error) {
            console.error(`Error archiving CRM profile ${profileId}:`, error);
            if (error.response?.status === 404) {
                throw keepServerAnswer(error, 'CRM profile not found');
            }
            if (error.response?.status === 400) {
                throw keepServerAnswer(error, error.response.data.message || 'Partner is already archived');
            }
            throw error;
        }
    },

    /**
     * Reactivate an archived CRM profile
     * @param {string|number} profileId - The CRM profile ID
     * @returns {Promise<Object>} - Reactivate response with details
     */
    reactivateProfile: async (profileId) => {
        try {
            if (!profileId) {
                throw new Error('Profile ID is required');
            }
            
            const response = await axiosInstance.post(`/api/crm/profiles/${profileId}/reactivate/`);
            
            console.log(`Successfully reactivated CRM profile ${profileId}`);
            console.log('Reactivate response:', response.data);
            
            return response;
        } catch (error) {
            console.error(`Error reactivating CRM profile ${profileId}:`, error);
            if (error.response?.status === 404) {
                throw keepServerAnswer(error, 'CRM profile not found');
            }
            if (error.response?.status === 400) {
                throw keepServerAnswer(error, error.response.data.message || 'Partner is not archived');
            }
            throw error;
        }
    },

    /**
     * Change approval status of a CRM profile (CRM Approvers only)
     * @param {string|number} profileId - The CRM profile ID
     * @param {Object} statusData - Status change data
     * @param {string} statusData.new_status - New approval status (pending|approved|rejected)
     * @param {string} statusData.rejection_reason - Rejection reason (required if status is rejected)
     * @param {string} statusData.notes - Optional additional notes
     * @returns {Promise<Object>} - Status change response with details
     */
    changeApprovalStatus: async (profileId, statusData) => {
        try {
            if (!profileId) {
                throw new Error('Profile ID is required');
            }
            
            if (!statusData.new_status) {
                throw new Error('New status is required');
            }
            
            console.log(`Changing approval status for CRM profile ${profileId} to ${statusData.new_status}`);
            
            const response = await axiosInstance.post(
                `/api/crm/profiles/${profileId}/change-approval-status/`,
                statusData
            );
            
            console.log(`Successfully changed approval status for CRM profile ${profileId}`);
            console.log('Status change response:', response.data);
            
            return response;
        } catch (error) {
            console.error(`Error changing approval status for CRM profile ${profileId}:`, error);
            if (error.response?.status === 404) {
                throw keepServerAnswer(error, 'CRM profile not found');
            }
            if (error.response?.status === 403) {
                throw keepServerAnswer(error, error.response.data.error || 'Only CRM Approvers can change profile approval status');
            }
            if (error.response?.status === 400) {
                throw keepServerAnswer(error, error.response.data.error || 'Invalid status change request');
            }
            throw error;
        }
    },

    /**
     * Advanced search for CRM profiles
     * @param {Object} searchCriteria - Advanced search criteria
     * @param {string} searchCriteria.legal_name - Legal entity name search
     * @param {string} searchCriteria.trading_name - Trading name search
     * @param {string} searchCriteria.kyc_status - KYC status filter
     * @param {number} searchCriteria.risk_rating_min - Minimum risk rating
     * @param {number} searchCriteria.risk_rating_max - Maximum risk rating
     * @param {Array<string>} searchCriteria.partner_types - Partner types filter
     * @param {Array<string>} searchCriteria.industries - Industries filter
     * @returns {Promise<Object>} - Search results
     */
    advancedSearch: async (searchCriteria) => {
        try {
            const params = new URLSearchParams();
            
            if (searchCriteria.legal_name) params.append('legal_name', searchCriteria.legal_name);
            if (searchCriteria.trading_name) params.append('trading_name', searchCriteria.trading_name);
            if (searchCriteria.kyc_status) params.append('kyc_status', searchCriteria.kyc_status);
            if (searchCriteria.risk_rating_min) params.append('risk_rating_min', searchCriteria.risk_rating_min);
            if (searchCriteria.risk_rating_max) params.append('risk_rating_max', searchCriteria.risk_rating_max);
            
            // Handle array parameters
            if (searchCriteria.partner_types && searchCriteria.partner_types.length > 0) {
                searchCriteria.partner_types.forEach(type => params.append('partner_types', type));
            }
            if (searchCriteria.industries && searchCriteria.industries.length > 0) {
                searchCriteria.industries.forEach(industry => params.append('industries', industry));
            }
            
            const url = `/api/crm/profiles/search/?${params.toString()}`;
            const response = await axiosInstance.get(url);
            
            console.log('Advanced search completed:', response.data.length || response.data.count || 'unknown count');
            return response.data;
        } catch (error) {
            console.error('Error in CRM advanced search:', error);
            throw error;
        }
    },

    /**
     * Run External Data analysis for a CRM partner
     * @param {string|number} partnerId - The CRM partner ID
     * @param {string} userGuidance - Optional user guidance for the analysis
     * @returns {Promise<Object>} - Analysis result
     */
    runExternalDataAnalysis: async (partnerId, userGuidance = '') => {
        try {
            if (!partnerId) {
                throw new Error('Partner ID is required');
            }

            console.log(`Running external data analysis for partner ${partnerId}`);
            console.log(`User guidance: ${userGuidance}`);

            const response = await axiosInstance.post(
                `/api/crm/partners/${partnerId}/external-data/analyze/`,
                { user_guidance: userGuidance }
            );

            console.log(`External data analysis completed for partner ${partnerId}`);
            return response.data;
        } catch (error) {
            console.error(`Error running external data analysis for partner ${partnerId}:`, error);
            throw error;
        }
    },

    /**
     * Get External Data analysis history for a CRM partner
     * @param {string|number} partnerId - The CRM partner ID
     * @returns {Promise<Object>} - Analysis history
     */
    getExternalDataAnalysisHistory: async (partnerId) => {
        try {
            if (!partnerId) {
                throw new Error('Partner ID is required');
            }

            console.log(`Fetching external data analysis history for partner ${partnerId}`);

            const response = await axiosInstance.get(
                `/api/crm/partners/${partnerId}/external-data/history/`
            );

            console.log(`Successfully fetched external data analysis history for partner ${partnerId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching external data analysis history for partner ${partnerId}:`, error);
            throw error;
        }
    },

    /**
     * Delete External Data analysis for a CRM partner
     * @param {string|number} partnerId - The CRM partner ID
     * @param {string} analysisId - The analysis ID to delete
     * @returns {Promise<Object>} - Delete result
     */
    deleteExternalDataAnalysis: async (partnerId, analysisId) => {
        try {
            if (!partnerId) {
                throw new Error('Partner ID is required');
            }

            if (!analysisId) {
                throw new Error('Analysis ID is required');
            }

            console.log(`Deleting external data analysis ${analysisId} for partner ${partnerId}`);

            const response = await axiosInstance.delete(
                `/api/crm/partners/${partnerId}/external-data/analysis/${analysisId}/delete/`
            );

            console.log(`Successfully deleted external data analysis ${analysisId} for partner ${partnerId}`);
            return response.data;
        } catch (error) {
            console.error(`Error deleting external data analysis ${analysisId} for partner ${partnerId}:`, error);
            throw error;
        }
    },

    /**
     * Build partner profile using HAMSTIR
     * @param {Object} data - Profile build data
     * @param {string} data.partner_identifier - Partner identifier (name, website, etc.)
     * @param {Array} data.selected_documents - Selected documents for analysis
     * @param {string|null} data.partner_id - Optional existing partner ID
     * @returns {Promise<Object>} - Build results
     */
    buildPartnerProfile: async (data) => {
        try {
            console.log('Building partner profile with HAMSTIR:', data);
            
            const response = await axiosInstance.post('/api/crm/build-partner-profile/', data);
            
            console.log('Successfully built partner profile:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error building partner profile:', error);
            throw error;
        }
    },

    /**
     * Get dropdown options for field mapping
     * @returns {Promise<Object>} - Dropdown options and field mappings
     */
    getDropdownOptions: async () => {
        try {
            console.log('Fetching dropdown options for field mapping');
            
            const response = await axiosInstance.get('/api/crm/dropdown-options/');
            
            console.log('Successfully fetched dropdown options:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching dropdown options:', error);
            throw error;
        }
    },

    /**
     * Apply selected profile data to existing profile
     * @param {Object} data - Application data
     * @param {string} data.partner_id - Partner ID
     * @param {Object} data.profile_data - Selected profile data to apply
     * @returns {Promise<Object>} - Application results
     */
    applyProfileData: async (data) => {
        try {
            console.log('Applying profile data:', data);
            
            const response = await axiosInstance.post('/api/crm/apply-profile-data/', data);
            
            console.log('Successfully applied profile data:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error applying profile data:', error);
            throw error;
        }
    },

    /**
     * Search CRM profiles by name (fuzzy search)
     * @param {string} searchTerm - Search term for legal entity name or trading name
     * @returns {Promise<Array>} - Array of matching profiles with {id, legal_entity_name, trading_short_name}
     */
    searchProfilesByName: async (searchTerm) => {
        try {
            if (!searchTerm || searchTerm.trim().length < 2) {
                return [];
            }

            console.log('Searching CRM profiles by name:', searchTerm);
            
            const response = await axiosInstance.get('/api/crm/profiles/', {
                params: { search: searchTerm.trim() }
            });
            
            // Extract relevant data from response
            let profiles = [];
            if (response.data && response.data.results) {
                // Paginated response
                profiles = response.data.results.map(profile => ({
                    id: profile.id,
                    legal_entity_name: profile.legal_entity_name || '',
                    trading_short_name: profile.trading_short_name || '',
                    base_currency: profile.base_currency || 'USD'
                }));
            } else if (Array.isArray(response.data)) {
                // Non-paginated response
                profiles = response.data.map(profile => ({
                    id: profile.id,
                    legal_entity_name: profile.legal_entity_name || '',
                    trading_short_name: profile.trading_short_name || '',
                    base_currency: profile.base_currency || 'USD'
                }));
            }
            
            console.log(`Found ${profiles.length} matching CRM profiles`);
            return profiles;
        } catch (error) {
            console.error('Error searching CRM profiles by name:', error);
            throw error;
        }
    },

    // === HR CONTACT IMPORT METHODS ===

    getHRContacts: async (profileId) => {
        try {
            const response = await axiosInstance.get(`/api/crm/profiles/${profileId}/hr-contacts/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching HR contacts for profile ${profileId}:`, error);
            throw error;
        }
    },

    importHRContacts: async (profileId, employeeIds) => {
        try {
            const response = await axiosInstance.post(`/api/crm/profiles/${profileId}/import-hr-contacts/`, {
                employee_ids: employeeIds
            });
            return response.data;
        } catch (error) {
            console.error(`Error importing HR contacts for profile ${profileId}:`, error);
            throw error;
        }
    },

    // === RFC (REQUEST FOR CHANGES) METHODS - PHASE 1 ===

    /**
     * Check if user can edit profile freely without submitting RFC
     * @param {number} profileId - The CRM profile ID
     * @returns {Promise<Object>} - Object with can_edit_freely boolean and reason
     */
    canEditFreely: async (profileId) => {
        try {
            console.log(`Checking edit permissions for CRM profile ${profileId}`);
            const response = await axiosInstance.get(`/api/crm/profiles/${profileId}/can-edit-freely/`);
            return response.data;
        } catch (error) {
            console.error(`Error checking edit permissions for profile ${profileId}:`, error);
            throw error;
        }
    },

    /**
     * Submit a change request for an approved profile
     * @param {number} profileId - The CRM profile ID
     * @param {Object} changesData - Object with field changes {field_name: {old: value, new: value}}
     * @param {string} notes - Optional notes explaining the changes
     * @returns {Promise<Object>} - Created change request data
     */
    submitChangeRequest: async (profileId, changesData, notes = '') => {
        try {
            console.log(`Submitting RFC for profile ${profileId} with ${Object.keys(changesData).length} changes`);
            const response = await axiosInstance.post(
                `/api/crm/profiles/${profileId}/submit-change-request/`,
                {
                    changes_data: changesData,
                    notes: notes
                }
            );
            console.log('RFC submitted successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error(`Error submitting RFC for profile ${profileId}:`, error);
            throw error;
        }
    },

    /**
     * Get all pending change requests for a profile
     * @param {number} profileId - The CRM profile ID
     * @returns {Promise<Object>} - Object with count and array of change requests
     */
    getPendingChangeRequests: async (profileId) => {
        try {
            console.log(`Fetching pending RFCs for profile ${profileId}`);
            const response = await axiosInstance.get(`/api/crm/profiles/${profileId}/pending-change-requests/`);
            console.log(`Found ${response.data.count} pending RFCs`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching pending RFCs for profile ${profileId}:`, error);
            throw error;
        }
    },

    /**
     * Get RFC history (approved/rejected) for a profile
     * @param {number} profileId - The CRM profile ID
     * @returns {Promise<Object>} - Object with count and array of reviewed change requests
     */
    getChangeRequestHistory: async (profileId) => {
        try {
            console.log(`Fetching RFC history for profile ${profileId}`);
            const response = await axiosInstance.get(`/api/crm/profiles/${profileId}/change-request-history/`);
            console.log(`Found ${response.data.count} reviewed RFCs in history`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching RFC history for profile ${profileId}:`, error);
            throw error;
        }
    },

    /**
     * Review a change request with field-level decisions
     * @param {number} profileId - The CRM profile ID
     * @param {number} rfcId - The RFC ID
     * @param {Object} fieldDecisions - Object with field decisions {field_name: {decision: 'approved'|'rejected', rejection_reason: ''}}
     * @returns {Promise<Object>} - Review result data
     */
    reviewChangeRequest: async (profileId, rfcId, fieldDecisions) => {
        try {
            console.log(`Submitting review for RFC ${rfcId} on profile ${profileId}`);
            const response = await axiosInstance.post(
                `/api/crm/profiles/${profileId}/review-change-request/${rfcId}/`,
                {
                    field_decisions: fieldDecisions
                }
            );
            console.log('RFC review submitted successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error(`Error reviewing RFC ${rfcId}:`, error);
            throw error;
        }
    }
};

export default crmService; 